import { useCallback } from "react";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import { Paginated, PaginationQuery, UpdateOrderRequestBody, UpdateVisibilityRequestBody } from "../../types";
import { buildUrl, mutateCache, useApi } from "../api";
import { CustomError } from "../errors";
import { InsertLowerElementBody, LowerElement, UpdateLowerElementBody } from "./types/lowerElements";

export const useLowerElementsApi = () => {
  const api = useApi();

  const getAdminLowerElement = useCallback(
    (signal: AbortSignal) => {
      const url = `/admin/settings/vertigrip/lower_elements`;
      return useSWRMutation<LowerElement, CustomError, string, string>(url, (url, { arg }: { arg: string }) =>
        api.get<LowerElement>({ url: `${url}/${arg}`, signal }),
      );
    },
    [api],
  );

  const useSteelVersionAdminLowerElements = useCallback(
    (steelVersionId: string, query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`/admin/settings/vertigrip/steel_versions/${steelVersionId}/lower_elements`, query);
      return useSWR<Paginated<LowerElement>, CustomError, string>(url, (url) =>
        api.get<Paginated<LowerElement>>({ url, signal }),
      );
    },
    [api],
  );

  const useInstallationTypeAdminLowerElements = useCallback(
    (installationTypeId: string, query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`/admin/settings/vertigrip/installation_types/${installationTypeId}/lower_elements`, query);
      return useSWR<Paginated<LowerElement>, CustomError, string>(url, (url) =>
        api.get<Paginated<LowerElement>>({ url, signal }),
      );
    },
    [api],
  );

  const insertAdminLowerElement = useCallback(
    async (body: InsertLowerElementBody): Promise<{ id: string }> => {
      const url = `/admin/settings/vertigrip/lower_elements`;
      const id = await api.post<{ id: string }, InsertLowerElementBody>(url, body);
      mutateCache([
        url,
        `/admin/settings/vertigrip/installation_types/${body.installationTypeId}/lower_elements`,
        `/admin/settings/vertigrip/steel_versions/${body.steelVersionId}/lower_elements`,
      ]);
      return id;
    },
    [api],
  );

  const updateAdminLowerElement = useCallback(
    async (lowerElementId: string, body: UpdateLowerElementBody): Promise<void> => {
      const url = `/admin/settings/vertigrip/lower_elements/${lowerElementId}`;
      await api.put<UpdateLowerElementBody>(url, body);
      mutateCache([
        `/admin/settings/vertigrip/lower_elements`,
        `/admin/settings/vertigrip/installation_types/${body.installationTypeId}/lower_elements`,
        `/admin/settings/vertigrip/steel_versions/${body.steelVersionId}/lower_elements`,
      ]);
    },
    [api],
  );

  const deleteAdminLowerElement = useCallback(
    async (lowerElementId: string, steelVersionId: string, installationTypeId: string): Promise<void> => {
      const url = `/admin/settings/vertigrip/lower_elements/${lowerElementId}`;
      await api.del(url);
      mutateCache([
        `/admin/settings/vertigrip/lower_elements`,
        `/admin/settings/vertigrip/lower_elements/${lowerElementId}`,
        `/admin/settings/vertigrip/installation_types/${installationTypeId}/lower_elements`,
        `/admin/settings/vertigrip/steel_versions/${steelVersionId}/lower_elements`,
        `/admin/settings/vertigrip/installation_types/${installationTypeId}/lower_elements/${lowerElementId}`,
        `/admin/settings/vertigrip/steel_versions/${steelVersionId}/lower_elements/${lowerElementId}`,
      ]);
    },
    [api],
  );

  const updateAdminLowerElementVisibility = useCallback(
    async (
      lowerElementId: string,
      visibility: UpdateVisibilityRequestBody,
      options?: { installationTypeId?: string; steelVersionId?: string },
    ): Promise<void> => {
      const url = `/admin/settings/vertigrip/lower_elements/${lowerElementId}/visibility`;
      await api.put<UpdateVisibilityRequestBody>(url, visibility);
      mutateCache([
        "/admin/settings/vertigrip/lower_elements",
        `/admin/settings/vertigrip/installation_types/${options?.installationTypeId}/lower_elements`,
        `/admin/settings/vertigrip/steel_versions/${options?.steelVersionId}/lower_elements`,
      ]);
    },
    [api],
  );

  const updateAdminLowerElementOrder = useCallback(
    async (
      lowerElementId: string,
      order: UpdateOrderRequestBody,
      options?: { installationTypeId?: string; steelVersionId?: string },
    ): Promise<void> => {
      const url = `/admin/settings/vertigrip/lower_elements/${lowerElementId}/order`;
      await api.put<UpdateOrderRequestBody>(url, order);
      mutateCache([
        "/admin/settings/vertigrip/lower_elements",
        `/admin/settings/vertigrip/installation_types/${options?.installationTypeId}/lower_elements`,
        `/admin/settings/vertigrip/steel_versions/${options?.steelVersionId}/lower_elements`,
      ]);
    },
    [api],
  );

  return {
    getAdminLowerElement,
    useSteelVersionAdminLowerElements,
    useInstallationTypeAdminLowerElements,
    insertAdminLowerElement,
    updateAdminLowerElement,
    updateAdminLowerElementVisibility,
    updateAdminLowerElementOrder,
    deleteAdminLowerElement,
  };
};
